/**
 * Client-side image downscaling for attachments that would otherwise
 * exceed the server's MEDIA_MAX_BYTES cap.
 *
 * Phone gallery shots are routinely 5-15 MB at full resolution; the
 * server caps individual uploads at 10 MB. Rather than fail the send,
 * decode the original via createImageBitmap, draw onto a canvas
 * scaled down to fit a reasonable max dimension, and re-encode as
 * JPEG at decreasing quality until we're under the budget. Pure
 * client-side; the original ciphertext + key path is unchanged.
 */

/** Server cap, mirrored — keep in sync with apps/worker/src/media/routes.ts. */
export const MEDIA_MAX_BYTES = 10 * 1024 * 1024;
/** Below this size the original is fine, no work needed. */
export const DOWNSCALE_THRESHOLD_BYTES = 8 * 1024 * 1024;
/** Maximum dimension on either side after downscale. */
const MAX_DIMENSION = 2048;
/** JPEG quality steps to try, descending. */
const QUALITY_STEPS = [0.85, 0.75, 0.65, 0.55] as const;

export interface DownscaleResult {
  readonly file: File;
  readonly originalSize: number;
  readonly scaled: boolean;
}

function isImage(file: File): boolean {
  return file.type.startsWith('image/');
}

async function imageToBitmap(file: File): Promise<ImageBitmap> {
  if (typeof createImageBitmap === 'undefined') {
    throw new Error('createImageBitmap not supported');
  }
  return createImageBitmap(file);
}

function fitDimensions(width: number, height: number, max: number): { w: number; h: number } {
  if (width <= max && height <= max) return { w: width, h: height };
  const ratio = width >= height ? max / width : max / height;
  return { w: Math.round(width * ratio), h: Math.round(height * ratio) };
}

async function canvasToBlob(canvas: HTMLCanvasElement, quality: number): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (b) => {
        if (b) resolve(b);
        else reject(new Error('canvas.toBlob returned null'));
      },
      'image/jpeg',
      quality,
    );
  });
}

/**
 * Returns the input file unchanged when it's already small enough or
 * not an image. Otherwise re-encodes it as JPEG and returns the new
 * File. Throws if even the lowest-quality JPEG still exceeds
 * MEDIA_MAX_BYTES — caller surfaces a "too large" error.
 */
export async function maybeDownscaleImage(file: File): Promise<DownscaleResult> {
  if (!isImage(file) || file.size <= DOWNSCALE_THRESHOLD_BYTES) {
    return { file, originalSize: file.size, scaled: false };
  }
  let bitmap: ImageBitmap;
  try {
    bitmap = await imageToBitmap(file);
  } catch {
    // No bitmap support → return as-is and let the upload either
    // succeed or fail on its own. The server still rejects with a
    // clean 413 if it's truly too large.
    return { file, originalSize: file.size, scaled: false };
  }
  const { w, h } = fitDimensions(bitmap.width, bitmap.height, MAX_DIMENSION);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) {
    bitmap.close?.();
    return { file, originalSize: file.size, scaled: false };
  }
  ctx.drawImage(bitmap, 0, 0, w, h);
  bitmap.close?.();
  for (const quality of QUALITY_STEPS) {
    const blob = await canvasToBlob(canvas, quality);
    if (blob.size <= MEDIA_MAX_BYTES) {
      const baseName = file.name.replace(/\.[^.]+$/, '') || 'image';
      const next = new File([blob], `${baseName}.jpg`, {
        type: 'image/jpeg',
        lastModified: Date.now(),
      });
      return { file: next, originalSize: file.size, scaled: true };
    }
  }
  throw new Error(
    `image is too large to send even after downscaling (original ${String(
      Math.ceil(file.size / 1024 / 1024),
    )} MB, max ${String(MEDIA_MAX_BYTES / 1024 / 1024)} MB)`,
  );
}
